import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Account, LimitationModalData, ModalInfo } from '../../../../shared/models';
import { SystemPages } from '../../../../shared/enums/routerPaths.enum';
import { AccountService } from '../../../../shared/services';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { filter, map, take } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { patchState } from 'src/app/shared/utilities/object-patch';

@Component({
  selector: 'app-limitation-modal',
  templateUrl: './limitation-modal.component.html',
  styleUrls: ['./limitation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LimitationModalComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<LimitationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LimitationModalData,
    private authService: AuthService,
    private accountService: AccountService,
  ) {}

  actionPaymentText = 'LIMITATION_MODAL.UPGRADE_PLAN';
  actionFreeTryText = 'LIMITATION_MODAL.TRY_FULL_VERSION';
  actionRouterLink: string = SystemPages.BILLING;

  info: ModalInfo;
  limitAccess: boolean;
  freeTariff: boolean;

  ready$: Observable<boolean>;

  ngOnInit(): void {
    this.ready$ = combineLatest([
      this.authService.isAdmin$,
      this.accountService.currentAccountInfo$.pipe(
        filter((account: Account) => !!account),
        take(1),
      ),
    ]).pipe(
      map(([isAdmin, account]) => {
        this.info = patchState(this.info, { hideCloseButton: this.data.hideCloseButton });

        this.limitAccess = this.accountService.getIsAccountBlocked(account);
        this.freeTariff = this.accountService.getIsFreeTariff(account);
        // Gate the Upgrade action on plan state (single choke point) so it also
        // fires for Limited resolved via dev mock / derivation, not just the raw
        // blocked/free flags. The flags above still drive the "try full version"
        // template branch (free-but-not-blocked), so they stay.
        const notAllowedAccess = this.accountService.getIsAccountLimited(account);

        this.info = {
          type: 'success-modal',
          closeModalBtnId: 'close-plan-limitation-m',
          hideCloseButton: this.data.hideCloseButton,
          header: {
            title: 'LIMITATION_MODAL.TITLE',
            text: 'LIMITATION_MODAL.PLAN.TEXT',
          },
        };

        switch (this.data.type) {
          case 'daily':
            this.info.header.text = notAllowedAccess
              ? 'LIMITATION_MODAL.PLAN.TEXT'
              : 'LIMITATION_MODAL.DAILY.TEXT';

            if (this.data.withAction || isAdmin) {
              this.actionPaymentText = isAdmin
                ? 'LIMITATION_MODAL.UPGRADE_PLAN'
                : 'LIMITATION_MODAL.GO_TO_SEARCH';
              this.actionRouterLink = isAdmin ? SystemPages.BILLING : SystemPages.SEARCH;
              this.info.actions = { submitWithClothing: { content: true } };
            }
            break;
          case 'plan':
            if ((this.data.withAction || notAllowedAccess) && !this.data.withoutAnyActions) {
              this.actionPaymentText = 'LIMITATION_MODAL.UPGRADE_PLAN';
              this.actionRouterLink = SystemPages.BILLING;
              this.info.actions = { submitWithClothing: { content: true } };
            }
            break;
        }
      }),
      map(() => true),
    );
  }

  closeModal(condition: boolean = false): void {
    if (condition || !this.data.hideCloseButton) {
      this.dialogRef.close(condition);
    }
  }
}
